// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// utils
import { fDate } from 'src/utils/format-time';
import { fShortenNumber } from 'src/utils/format-number';
// types
import { HTBaiViet } from 'src/types/htblog';
// assets
import { AvatarShape } from 'src/assets/illustrations';
// components
import Image from 'src/components/image';
import Iconify from 'src/components/iconify';
import TextMaxLine from 'src/components/text-max-line';

// ----------------------------------------------------------------------

type Props = {
  post: HTBaiViet;
  index?: number;
};

export default function BlogItem({ post, index }: Props) {
  const theme = useTheme();

  const mdUp = useResponsive('up', 'md');

  const {
    slug,
    hinhAnh: main_image,
    tieuDe: title,
    luotXem: view_count,
    htTaiKhoan,
    ngayTao: date,
    moTaNgan: short_description
  } = post;
  const comment_count = 0;
  const like_count = 0;
  const author_name = htTaiKhoan?.tenTaiKhoan || '';

  const latestPost = index === 0 || index === 1 || index === 2;

  if (mdUp && latestPost) {
    return (
      <Card>
        <Avatar
          alt={author_name}
          src={`/assets/images/avatar/avatar_${Math.floor(Math.random() * 24) + 1}.jpg`}
          sx={{
            top: 24,
            left: 24,
            zIndex: 9,
            position: 'absolute',
          }}
        />

        <BlogContent
          title={title}
          createdAt={date}
          totalViews={view_count}
          totalComments={comment_count}
          totalShares={like_count}
          index={index}
          linkTo={paths.blog.details(slug)}
        />

        <OverlayStyle />

        <Image
          alt={title}
          src={main_image}
          sx={{ height: 360, width: '100%' }}
          overlay={`linear-gradient(to bottom, ${alpha(theme.palette.grey[900], 0)} 0%, ${theme.palette.grey[900]
            } 75%)`}
        />
      </Card>
    );
  }

  return (
    <Card>
      <Box sx={{ position: 'relative' }}>
        <AvatarShape
          sx={{
            left: 0,
            right: 0,
            zIndex: 10,
            mx: 'auto',
            bottom: -26,
            position: 'absolute',
          }}
        />

        <Avatar
          alt={author_name}
          src={`/assets/images/avatar/avatar_${Math.floor(Math.random() * 24) + 1}.jpg`}
          sx={{
            left: 0,
            right: 0,
            zIndex: 11,
            mx: 'auto',
            bottom: -16,
            position: 'absolute',
          }}
        />

        <Image alt={title} src={main_image} ratio="4/3" />
      </Box>

      <BlogContent
        title={title}
        createdAt={date}
        totalViews={view_count}
        totalComments={comment_count}
        totalShares={like_count}
        index={index}
        linkTo={paths.blog.details(slug)}
      />
    </Card>
  );
}

// ----------------------------------------------------------------------

type BlogContentProps = {
  title: string;
  createdAt: string | Date;
  totalViews: number;
  totalComments: number;
  totalShares: number;
  index?: number;
  linkTo: string;
};

export function BlogContent({
  title,
  createdAt,
  totalViews,
  totalComments,
  totalShares,
  index,
  linkTo,
}: BlogContentProps) {
  const theme = useTheme();

  const mdUp = useResponsive('up', 'md');

  const latestPostLarge = index === 0 && mdUp;
  const latestPostSmall = (index === 1 || index === 2) && mdUp;

  const POST_INFO = [
    { number: totalComments, icon: 'eva:message-circle-fill' },
    { number: totalViews, icon: 'eva:eye-fill' },
    { number: totalShares, icon: 'eva:heart-fill' },
  ];

  return (
    <CardContent
      sx={{
        pt: 6,
        width: 1,
        ...((latestPostLarge || latestPostSmall) && {
          pt: 0,
          zIndex: 9,
          bottom: 0,
          position: 'absolute',
          color: 'common.white',
        }),
      }}
    >
      <Typography
        gutterBottom
        variant="caption"
        component="div"
        sx={{
          color: latestPostLarge || latestPostSmall ? 'common.white' : 'text.disabled',
          ...((latestPostLarge || latestPostSmall) && {
            opacity: 0.64,
          }),
        }}
      >
        {fDate(createdAt)}
      </Typography>

      <Link component={RouterLink} href={linkTo} color="inherit">
        <TextMaxLine
          variant={mdUp && latestPostLarge ? 'h5' : 'subtitle2'}
          line={2}
          persistent
          sx={{
            color: 'text.width',
            '&:hover': {
              color: 'primary.main',
            },
          }}
        >
          {title}
        </TextMaxLine>
      </Link>
      <Stack
        flexWrap="wrap"
        direction="row"
        justifyContent="flex-end"
        sx={{
          mt: 3,
          color: latestPostLarge || latestPostSmall ? 'common.white' : 'text.disabled',
          ...((latestPostLarge || latestPostSmall) && {
            opacity: 0.64,
          }),
        }}
      >
        {POST_INFO.map((info, _index) => (
          <Stack
            key={_index}
            direction="row"
            alignItems="center"
            sx={{ typography: 'caption', ml: _index === 0 ? 0 : 1.5 }}
          >
            <Iconify icon={info.icon} width={16} sx={{ mr: 0.5 }} />
            {fShortenNumber(info.number)}
          </Stack>
        ))}
      </Stack>
    </CardContent>
  );
}

// ----------------------------------------------------------------------

const OverlayStyle = () => (
  <Box
    sx={{
      top: 0,
      zIndex: 1,
      width: 1,
      height: 1,
      position: 'absolute',
      backgroundImage: (theme) =>
        `linear-gradient(to bottom, ${alpha(theme.palette.grey[900], 0)} 0%, ${theme.palette.grey[900]
        } 75%)`,
    }}
  />
);